import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import hero from "@/assets/awning-striped-fabric.jpg";
import heroPicture from "@/assets/awning-striped-fabric.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import shareImage from "@/assets/awning-blue-striped-fabric.jpg";
import blueCanvas from "@/assets/awning-blue-canvas.jpg";
import stripedFabric from "@/assets/awning-striped-fabric.jpg";
import bradzoilCanopy from "@/assets/bradzoil-yellow-canopy-awning.jpg";
import sanMarketStorefront from "@/assets/san-market-green-stripe-awning-night.jpg";
import sanMarketFrame from "@/assets/san-market-awning-frame-underside.jpg";
import sanMarketInstall from "@/assets/san-market-awning-install-ladder.jpg";
import mainDome from "@/assets/400-s-main-dome-awning-shop.jpg";
import sunbrellaBlueStripe from "@/assets/sunbrella-blue-stripe-fabric.jpg";
import sunbrellaMayfield from "@/assets/sunbrella-mayfield-black-white-stripe.jpg";
import logoComposite from "@/assets/texas-made-shade-logo-composite.png.asset.json";

export const Route = createFileRoute("/fabric-awnings")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Fabric Awnings in Texas — Sunbrella & Tempotest Canvas | Texas Made Shade",
      description: "Custom fabric awnings using Sunbrella® and Tempotest® premium canvas. Patterned, sewn, and installed in Texas.",
      image: shareImage,
      imageAlt: "Custom fabric awning in striped Sunbrella canvas",
      socialTitle: "Fabric Awnings — Sunbrella & Tempotest | Texas Made Shade",
      socialDescription: "Premium canvas awnings sewn and installed in Texas.",
      keywords: "fabric awnings Texas, Sunbrella awnings, Tempotest awnings, canvas awnings, fabric awnings Austin",
    }),
    scripts: serviceHeadScripts({
      path: "/fabric-awnings",
      serviceName: "Fabric Awnings",
      breadcrumbName: "Fabric Awnings",
      description: "Custom fabric awnings in Sunbrella and Tempotest canvas — residential and commercial, fabricated in Seguin, TX.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/fabric-awnings"
      serviceName="Fabric Awnings"
      eyebrow="★ FABRIC AWNINGS ★"
      title="Fabric Awnings — Sunbrella® & Tempotest® Canvas, Built in Texas"
      intro="Custom canvas awnings sewn in our Seguin shop using Sunbrella® and Tempotest® premium fabrics. Welded aluminum frame, hand-patterned canvas, installed by the same crew that built it."
      heroImage={heroPicture}
      features={[
        "Sunbrella® and Tempotest® premium acrylic canvas",
        "Hundreds of solid colors, stripes and patterns",
        "10-year color-fast canvas warranty",
        "Welded aluminum frame — not bolted prefab",
        "Patterned on-site for a perfect fit",
        "Re-cover service for existing frames",
      ]}
      body={[
        { heading: "Why Sunbrella & Tempotest?", text: "These are the only fabrics we trust on Texas projects. Solution-dyed acrylic resists UV fade, mildew and water — and stays soft and color-true for a decade or more in direct sun." },
        { heading: "Patterns, Stripes & Solids", text: "Pick from hundreds of canvas colors. Match your home, your brand, or go classic awning stripe. We'll bring fabric samples to your project." },
        { heading: "Re-Cover Service", text: "Have an existing awning frame in good shape? We can pattern, sew and install a fresh canvas re-cover for a fraction of the cost of a full replacement." },
      ]}
      applications={["Patios", "Storefronts", "Windows", "Restaurants", "RV Pads", "Outdoor Bars", "Porches"]}
      faqs={[
        { q: "How long does a fabric awning last in Texas?", a: "Sunbrella and Tempotest canvas typically holds color and structural integrity for 8–12 years in direct Texas sun. Our welded aluminum frames last 20+ years and can be re-skinned multiple times." },
        { q: "Can I get a custom color or stripe?", a: "Yes. Sunbrella alone offers hundreds of solid and patterned canvas options, and we stock and special-order both Sunbrella and Tempotest for any project." },
        { q: "Do you do canvas re-covers only?", a: "Yes. If your existing awning frame is structurally sound, we'll re-skin it with new Sunbrella canvas — much cheaper than buying a new awning." },
      ]}
      gallerySectionPlacement="beforeLetsTalk"
      gallerySection={{
        eyebrow: "Canvas in the Shop",
        title: "Sunbrella & Tempotest Fabric Work",
        images: [
          {
            src: sunbrellaBlueStripe,
            alt: "Marine-blue and white awning-stripe Sunbrella® acrylic canvas being fed through a Juki industrial sewing machine for a custom Texas patio awning at the Texas Made Shade shop in Seguin, TX",
            label: "Sunbrella® Marine Blue Stripe",
            caption: "Solution-dyed Sunbrella® awning stripe — fade-resistant, mildew-resistant, sewn on the Juki for a Central Texas patio cover.",
          },
          {
            src: sunbrellaMayfield,
            alt: "Black and white block-stripe Sunbrella® Mayfield Collection outdoor awning fabric rolled out on the cutting table for patterning at Texas Made Shade & Covers in Seguin, Texas",
            label: "Sunbrella® Mayfield · Black & White",
            caption: "Sunbrella® Mayfield Collection black-and-white awning stripe — bold contrast for storefront, restaurant, and porch awnings across Texas.",
          },
          { src: blueCanvas, alt: "Blue Sunbrella canvas awning panel" },
          { src: shareImage, alt: "Blue striped Sunbrella canvas rolls" },
          { src: bradzoilCanopy, alt: "Custom yellow fabric canopy awning fabricated and installed by Texas Made Shade for Bradzoil 10 Minute Oil Change in the New Braunfels / Seguin / San Marcos area", label: "Bradzoil · Branded Fabric Canopy" },
          { src: sanMarketStorefront, alt: "Custom green-and-white striped Sunbrella canvas storefront awning installed by Texas Made Shade over The San Market gift shop in downtown San Marcos, Texas", label: "San Market · Green Stripe Storefront", caption: "Classic green-and-white awning stripe Sunbrella® canvas — downtown San Marcos storefront." },
          { src: sanMarketFrame, alt: "Underside view of welded aluminum awning frame with green and white striped Sunbrella fabric stretched tight over a downtown storefront", label: "Welded Aluminum Frame · Underside", caption: "TIG-welded aluminum frame with stretched canvas — built to hold shape under Texas wind and rain." },
          { src: mainDome, alt: "Custom grey Sunbrella canvas half-dome entry awning with hand-painted black '400 S. Main' lettering, fabricated in the Texas Made Shade Seguin canvas shop in front of the Sunbrella color and marine vinyl swatch boards", label: "400 S. Main · Custom Dome Awning", caption: "Custom dome entry awning in Sunbrella® canvas — hand-painted lettering, built in the Seguin shop." },
          { src: sanMarketInstall, alt: "Texas Made Shade installer on a ladder fitting striped Sunbrella canvas to a welded aluminum awning frame at The San Market storefront in downtown San Marcos", label: "San Market · Canvas Install", caption: "On-site canvas install — patterned and stretched by the same crew that built the frame." },
        ],
      }}
      letsTalkImageSrc={logoComposite.url}
      letsTalkImageAlt="Texas Made Shade & Covers — custom made, built to last: shade and covers, sail shades, tension structures, covers, boat covers, sewing & welding"
    />
  ),
});
